import moment from 'moment'
import Head from 'next/head'
import Link from 'next/link'
import { useRouter } from 'next/router'
import React from 'react'
import 'react-dates/initialize'
import ReactHtmlParser from 'react-html-parser'
import Lightbox from 'react-image-lightbox'
import { Alert, Button, Col, Container, Media, Row, Spinner } from 'reactstrap'
import {
  BookingsRetreatsApi,
  InboxApi,
  RetreatsApi,
  ReviewsApi,
  UsersApi,
  WaitingListStatus
} from 'typescript-axios'
import {
  firstLetter,
  getCurrencySign,
  getLoginRedirectLink,
  getFormattedDateAndRange,
  offsetDate,
  getFormattedDateTime
} from 'common'
import GuruModal from '../../../components/GuruModal'
import ListingIcons from '../../../components/ListingIcons'
import Map from '../../../components/Map'
import NewMessageModal from '../../../components/NewMessageModal'
import PremiumModal from '../../../components/PremiumModal'
import Reviews from '../../../components/Reviews'
import ShowMore from '../../../components/ShowMore'
import SigninModal from '../../../components/SigninModal'
import Stars from '../../../components/Stars'
import { UserContext } from '../../../contexts/UserContext'
import WithLocale from '../../../hocs/WithLocale'
import useTranslation from '../../../hooks/UseTranslation'
import * as fbq from '../../../lib/fpixel'
import { translate } from '../../../translations/utils'
import {
  callWithoutCache,
  getAxiosParams,
  getListings
} from '../../../utils/client'

import { randomPosition } from '../../../utils/map'
import { redirectServer } from '../../../utils/session'
import { GuruReferralCta } from '../../../components/GuruReferralForUser'
import { getFirstClassDiscountBadge } from '../../marketing'
import BookButton, {
  BOOK_CALLBACK,
  BOOK_CALLBACK_FREE_CLASS,
  CONTACT_CALLBACK,
  JOIN_LIST_CALLBACK
} from 'components/BookButton'
import SwiperGallery from 'components/SwiperGallery'

const RETREAT_SERVICE = new RetreatsApi(getAxiosParams())
const USER_SERVICE = new UsersApi(getAxiosParams())
const REVIEWS_SERVICE = new ReviewsApi(getAxiosParams())
const BOOKINGS_SERVICE = new BookingsRetreatsApi(getAxiosParams())
const INBOX_SERVICE = new InboxApi(getAxiosParams())

const CUSTOM_STYLES = {
  overlay: {
    zIndex: '1000'
  },
  bodyOpen: {
    position: 'fixed'
  }
}

export async function getStaticProps({ params, locale, defaultLocale }) {
  let is404 = false
  const retreat = await callWithoutCache(
    (slug, opts) =>
      RETREAT_SERVICE.readRetreatApiV1RetreatsIdOrSlugGet(slug, opts),
    params.slug
  )
    .then((res) => res.data)
    .catch((e) => {
      if (e.response.status == 404) {
        is404 = true
      } else {
        console.error(e)
      }
    })
  if (is404) {
    return redirectServer(`/${locale}/404`, true)
  }

  const reviews = await REVIEWS_SERVICE.readReviewsApiV1ReviewsGet(
    undefined,
    undefined,
    retreat.owner.id
  ).then((res) => res.data)
  return {
    props: {
      nav: {
        light: true,
        classes: 'shadow',
        color: 'white'
      },
      title: retreat.title,
      data: { retreat, reviews },
      locale,
      defaultLocale
    },
    revalidate: 180
  }
}

export async function getStaticPaths() {
  return {
    paths: [],
    fallback: 'blocking'
  }
}

const Index = (props) => {
  const router = useRouter()
  const [range] = React.useState([{ startDate: new Date() }, { endDate: '' }])
  const { t } = useTranslation()
  const [isBooked, setIsBooked] = React.useState(null)
  const [waitingListStatus, setWaitingListStatus] = React.useState(null)
  const [showMessageModal, setShowMessageModal] = React.useState(false)
  const [showSigninModal, setShowSigninModal] = React.useState(false)
  const [signinCallback, setSigninCallback] = React.useState(null)
  const [coverBoxOpen, setCoverBoxOpen] = React.useState(false)
  const [pptyBoxOpen, setPptyBoxOpen] = React.useState(false)
  const [showPremium, setShowPremium] = React.useState(false)
  const [isLoading, setIsLoading] = React.useState(false)
  const [chat, setChat] = React.useState(null)
  const [modalContent, setModalContent] = React.useState({ isOpen: false })
  const { loggedUser, guruCode } = React.useContext(UserContext)

  const [referrerCode, setReferrerCode] = React.useState()

  if (router.isFallback) {
    return <div />
  }

  const { retreat, reviews } = props.data

  if (!retreat) {
    if (typeof window !== 'undefined') {
      router.replace('/404')
    }

    return <p />
  }
  const { owner: guru } = retreat

  const checkCode = () =>
    USER_SERVICE.readGuruWithGuruCodeApiV1UsersGuruCodesGuruCodeGet(guruCode)
      .then((res) => {
        if (!!res.data && res.data.id != guru.id) {
          return guruCode
        }
      })
      .catch(console.error)

  const isFull = retreat.num_participants == retreat.max_participants

  const { dateStartFormatted, timeRangeFormatted } =
    getFormattedDateAndRange(retreat)
  const isOwnBooking = loggedUser && loggedUser.id == retreat.owner.id
  // need to do the diff without TZ
  const isPast = moment().diff(moment(offsetDate(retreat.datetime_start))) > 0
  React.useEffect(() => {
    if (retreat && isFull) {
      RETREAT_SERVICE.readAmIInWaitingListApiV1RetreatsIdWaitingListMeGet(
        retreat.id
      ).then((res) => setWaitingListStatus(res.data.status))
    }
    const _isBooked = async () => {
      if (props.data && loggedUser) {
        const isBooked =
          await BOOKINGS_SERVICE.readRetreatBookingsByMeApiV1BookingsRetreatsByMeGet(
            retreat.id,
            undefined,
            undefined,
            undefined,
            'ok'
          )
            .then((res) => res.data.length > 0)
            .catch(() => false)
        setIsBooked(isBooked)
      } else if (!loggedUser) setIsBooked(false)
    }
    const _hasChat = async () => {
      if (!isOwnBooking && props.data && loggedUser) {
        const chat = await INBOX_SERVICE.getChatWithUserApiV1InboxWithUserGet(
          retreat.owner.id
        )
          .then((res) => res.data)
          .catch(() => false)
        setChat(chat)
      } else if (!loggedUser) {
        setChat(false)
      }
    }
    _isBooked()
    _hasChat()
  }, [loggedUser, props.data])

  // React.useEffect(() => {
  //     Map = dynamic(
  //         () => import('../../../components/Map'),
  //         { ssr: false }
  //     )
  //     setMapLoaded(true)

  //     setTap(size.width > 700 ? true : false)
  //     setDragging(size.width > 700 ? true : false)
  // }, [])

  // If the page is not yet generated, this will be displayed
  // initially until getStaticProps() finishes running

  if (router.isFallback) return <p />

  const getContactButton = () => {
    if (isOwnBooking || chat === null) return <div />
    const onClick =
      !chat && loggedUser
        ? () => setShowMessageModal(true)
        : () => {
            setShowSigninModal(true)
            setSigninCallback(CONTACT_CALLBACK)
          }
    const button = (
      <Button color='primary' onClick={onClick}>
        <i className='far fa-paper-plane mr-2' />
        {t('class_detail_contact_guru')}
      </Button>
    )

    if (chat) {
      const redirectUrl = `/inbox/${chat.id}`
      return getLoginRedirectLink(loggedUser, redirectUrl, button)
    }
    return button
  }

  const bookCallback = async () => {
    // const bd = await LISTINGS_SERVICE.getPriceApiV1ListingsPriceGet(
    //   listing.id
    // ).then((res) => res.data)
    // const isFree = bd.total == 0
    // if (isFree) {
    //   return bookCallbackFree()
    // }

    const { code } = router.query
    const query = { id: retreat.id }
    if (code) query.code = code
    router.push({ pathname: '/book/retreat/[id]', query })
  }

  const joinListCallback = async () => {
    setIsLoading(true)
    RETREAT_SERVICE.subscribeToWaitingListApiV1RetreatsIdWaitingListSubscribePost(
      retreat.id
    )
      .then(() => {
        setWaitingListStatus(WaitingListStatus.InList)
        setModalContent((modelContent) => ({
          ...modelContent,
          isOpen: true,
          msg: t('details_joined_waiting_list'),
          title: t('modal_title_success')
        }))
      })
      .catch((e) => {
        console.error(e)
        setModalContent((modelContent) => ({
          ...modelContent,
          isOpen: true,
          msg: t('modal_error_generic'),
          title: t('modal_title_oops')
        }))
      })
      .finally(() => setIsLoading(false))
  }

  const callback = () => {
    if (signinCallback == BOOK_CALLBACK) {
      bookCallback()
    } else if (signinCallback == CONTACT_CALLBACK) {
      setShowMessageModal(true)
    } else if (signinCallback == JOIN_LIST_CALLBACK) {
      joinListCallback()
    }
    setShowSigninModal(false)
  }

  const getLeftColumn = () => (
    <Col md='8' className='order-last order-md-first'>
      <div className='text-block'>
        <p className='text-primary'>
          <i className='fa-map-marker-alt fa mr-1' />
          &nbsp;
          {firstLetter(retreat.location.city)}
        </p>
        {retreat.title && (
          <h1 className='text-first-letter'>{retreat.title}</h1>
        )}
        {
          <div className='text-muted text-first-letter mb-4'>
            {retreat.activities.map((a) => firstLetter(a.name)).join(', ')}
          </div>
        }
        <ListingIcons listing={retreat} />
        {retreat.desc && (
          <div className='text-block'>
            {retreat.desc?.split('\n').map((p, i) => (
              <p key={i} className='text-muted font-weight-light'>
                {p}
              </p>
            ))}
          </div>
        )}
        {retreat.arrangements && (
          <div className='text-block'>
            <h3 className='mb-3 text-first-letter'>
              {t('retreat_arrangement_detail_title_about_the_class')}
            </h3>
            {retreat.arrangements.map((arr, i) => (
              <>
                <div className='d-flex justify-content-between'>
                  <h5 className='mb-3 text-first-letter'>{arr.name}</h5>
                  <h5 className='text-primary'>
                    {/* fixme: currency harcoded */}
                    {arr.price}&nbsp;{getCurrencySign('EUR')}
                  </h5>
                </div>
                {arr.desc && (
                  <p
                    key={`arrangements_${i}`}
                    className='text-muted font-weight-light'
                  >
                    {arr.desc}
                  </p>
                )}
              </>
            ))}
          </div>
        )}
        {retreat.images?.length > 1 && (
          <div className='text-block'>
            <h3 className='mb-3 text-first-letter'>{t('retreat_gallery')}</h3>
            <SwiperGallery data={retreat.images} />
          </div>
        )}
        <div className='text-block'>
          <h3 className='mb-3 text-first-letter'>
            {t('class_detail_title_about_the_place')}
          </h3>
          {retreat?.address_url && (
            <Link href={retreat?.address_url}>{t('see_more')}</Link>
          )}
          <div>
            <p>{retreat.address}</p>
            <p className='text-muted'>{retreat.address_complement}</p>
            <div className='map-wrapper-300 mb-3'>
              <Map
                className='h-100'
                center={[retreat.lat, retreat.lng]}
                circlePosition={[retreat.lat, retreat.lng]}
                zoom={16}
              />
            </div>
          </div>
        </div>
        {guru && (
          <div className='text-block'>
            <Media>
              <Link href='/users/[slug]' as={`/users/${guru.slug}`}>
                <img
                  src={guru.avatar ?? '/content/img/avatar/dummy-user.jpg'}
                  alt={guru.name_display}
                  className='avatar avatar-lg mr-4 pointer'
                />
              </Link>
              <Media body>
                <p className='d-flex flex-column'>
                  <span className='text-muted test-first-letter text-sm'>
                    {t('retreat_detail_taught_by')}
                  </span>

                  <Link href='/users/[slug]' as={`/users/${guru.slug}`}>
                    <a className='text-reset'>
                      <strong className='mr-2'>{`${guru.name_display}`}</strong>
                    </a>
                  </Link>
                  <span>
                    {(retreat.owner.n_reviews > 5 ||
                      retreat.owner.rating > 3) && (
                      <Stars stars={retreat.owner.rating} size='sm' />
                    )}
                  </span>
                </p>
                <p className='text-sm mb-0'>
                  <Link href='/users/[slug]' as={`/users/${guru.slug}`}>
                    <a>
                      {t('class_detail_other_listings', guru?.name_display)}{' '}
                      <i className='fa fa-long-arrow-alt-right ml-2' />
                    </a>
                  </Link>
                </p>
              </Media>
            </Media>
          </div>
        )}
      </div>
      {reviews && reviews.length > 0 && (
        <Reviews user={guru} reviews={reviews} />
      )}
    </Col>
  )

  const priceMin = retreat.arrangements.reduce(
    (p, arr) => Math.min(arr.price, p),
    10000000000000000
  )

  const start = getFormattedDateTime(retreat.datetime_start, true)
  const end = getFormattedDateTime(retreat.datetime_end, true)
  const initiateCheckout = () => {
    fbq.event('InitiateCheckout', {
      currency: 'EUR',
      value: retreat.arrangements[0].price,
      content_category: 'RETREAT',
      content_ids: [retreat.owner.slug, retreat.id, loggedUser?.slug]
    })
  }
  const getRightColumn = () => (
    <Col md='4' className='mb-4 order-first order-md-last'>
      <div style={{ top: '100px' }} className='ml-lg-4 sticky-top'>
        {retreat.num_participants > 1 && !isPast && (
          <Alert color='warning' className='text-sm'>
            <Media className='align-items-center'>
              <svg className='svg-icon svg-icon svg-icon-light w-2rem h-2rem mr-3 text-reset'>
                <use xlinkHref='/content/svg/orion-svg-sprite.svg#heart-1' />
              </svg>
              <Media
                dangerouslySetInnerHTML={{
                  __html: t('retreat_book_people_mind')
                }}
                body
              />
            </Media>
          </Alert>
        )}
        <div className='p-4 shadow rounded'>
          <div className='text-block pb-3' style={{ borderBottom: '0px' }}>
            <div className='text-muted d-flex justify-content-end align-items-center'>
              <span className='mr-2'>{firstLetter(t('starting_from'))}</span>
              <span className='text-primary h2'>
                {t('price_display', priceMin, getCurrencySign('EUR'))}
              </span>
            </div>
          </div>
          <div className='text-block py-3'>
            <ul className='list-unstyled mb-0'>
              <li className='mb-3'>
                <i className='far fa-calendar fa-fw text-muted mr-2' />
                {start.date} {start.time}
                <i className='fas fa-arrow-right fa-fw text-muted mx-2' />
                {end.date} {end.time}
              </li>
              <li className='mb-3'>
                <i className='fas fa-users fa-fw text-muted mr-2' />
                {t(
                  'x_out_of_y_booked',
                  retreat.num_participants,
                  retreat.max_participants
                )}
              </li>
              <li className='mb-0'>
                <i className={`fas fa-home fa-fw text-muted mr-2`} />
                {retreat.location.city}
              </li>
            </ul>
          </div>
          <BookButton
            isBooked={isBooked}
            isDeleted={retreat.is_deleted}
            isFull={isFull}
            isOwnBooking={isOwnBooking}
            joinListCallback={joinListCallback}
            setShowSigninModal={setShowSigninModal}
            setSigninCallback={setSigninCallback}
            bookCallback={bookCallback}
            waitingListStatus={waitingListStatus}
            isPast={isPast}
            isLoading={isLoading}
            setShowPremium={setShowPremium}
            setModalContent={setModalContent}
            initiateCheckout={initiateCheckout}
          />
          <div className='text-center mt-2'>
            <GuruReferralCta retreat={retreat} />
          </div>
        </div>
      </div>
    </Col>
  )

  const coverUrl =
    retreat.cover ?? `${process.env.NEXT_PUBLIC_HOST}/content/img/oglogo.png`

  const getHead = () => {
    try {
      return (
        retreat && (
          <Head>
            <meta property='og:image' content={coverUrl} key='og:image' />
            <meta
              property='og:url'
              content={`${process.env.NEXT_PUBLIC_HOST}/retreats/${retreat.slug}`}
              key='og:url'
            />
            <meta
              property='og:title'
              content={`${retreat.title} | ${retreat.owner.name_display}`}
              key='og:title'
            />
            <meta
              property='og:description'
              content={`${retreat.desc}\n${retreat.activities
                .map((a) => a.name)
                .join(' | ')} retreat retraite ${retreat.location.city}`}
              key='og:description'
            />
          </Head>
        )
      )
    } catch (e) {
      console.error(e)
      return ''
    }
  }

  return (
    <>
      <PremiumModal
        isOpen={showPremium}
        toggle={() => setShowPremium((p) => !p)}
      />
      {coverBoxOpen && coverUrl && (
        <Lightbox
          mainSrc={coverUrl}
          onCloseRequest={() => setCoverBoxOpen(false)}
          enableZoom={false}
          reactModalStyle={CUSTOM_STYLES}
        />
      )}
      {/* {pptyBoxOpen && retreat?.cover && (
        <Lightbox
          mainSrc={retreat?.cover}
          onCloseRequest={() => setPptyBoxOpen(false)}
          enableZoom={false}
          reactModalStyle={CUSTOM_STYLES}
        />
      )} */}
      {getHead()}
      <SigninModal
        isOpen={showSigninModal && !loggedUser}
        toggle={() => {
          setSigninCallback(null)
          setShowSigninModal(false)
        }}
        showProgress
        callback={callback}
      />
      <NewMessageModal
        isOpen={showMessageModal}
        toggle={() => setShowMessageModal(!showMessageModal)}
        user={retreat.owner}
        onFinish={() => setShowMessageModal(false)}
      />
      <GuruModal
        isOpen={modalContent.isOpen}
        toggle={() => setModalContent({ ...modalContent, isOpen: false })}
        title={modalContent.title}
        button={modalContent.button ?? undefined}
        onClosed={modalContent.onClosed}
      >
        {modalContent.msg}
      </GuruModal>
      <section>
        <Container>
          <div>
            <img
              onClick={() => setCoverBoxOpen(true)}
              src={coverUrl}
              className='class-cover rounded-bottom mb-3 d-none d-sm-inline pointer'
            />
          </div>
          <Row>
            {getLeftColumn()}
            {getRightColumn()}
          </Row>
        </Container>
      </section>
    </>
  )
}

export default WithLocale(Index)
